import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * P2.4 T3 — athlete-side ⌘K command bar.
 *
 * Mirrors the Workstation CommandPalette UX (cmd-K opens, arrow keys
 * navigate, Enter fires, Esc closes) but is scoped to the routing
 * surface an athlete actually uses: Today / Plan / Race / Chat /
 * Workstation / Settings / Profile. No theme / accent / preset
 * manipulation here — that's Workstation's own concern.
 *
 * Mounted at the layout level so a ⌘K from any athlete page opens
 * the same palette. Keyboard shortcut is the desktop-canon combo
 * (Cmd on Mac, Ctrl on Windows/Linux). Mobile users see no chrome
 * for it — the trigger is keyboard-only by canon, in line with the
 * "athlete-mobile-first, palette is desktop-power" decision.
 */

interface CommandItem {
  id: string;
  label: string;
  hint: string;
  keywords: string[];
  path: string;
}

export interface AthleteCommandBarProps {
  /** Override the trigger so tests can drive open state without firing
   *  the keyboard combo. Optional. */
  isOpen?: boolean;
  onClose?: () => void;
}

export function AthleteCommandBar({
  isOpen: controlledOpen,
  onClose,
}: AthleteCommandBarProps = {}) {
  const { t } = useTranslation("common");
  const nav = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = (v: boolean) => {
    if (controlledOpen === undefined) setInternalOpen(v);
    if (!v) onClose?.();
  };

  // Keyboard trigger: Cmd/Ctrl + K opens the palette from any focus state
  // except inputs (so typing K in a chat textarea doesn't pop the bar).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement | null)?.tagName;
      const isEditing =
        tag === "INPUT" || tag === "TEXTAREA" ||
        (e.target as HTMLElement | null)?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        if (isEditing) return;
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && isOpen) {
        e.preventDefault();
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery("");
      setActiveIdx(0);
    }
  }, [isOpen]);

  const items: CommandItem[] = useMemo(
    () => [
      // IA_CANON_v2 2026-07-04 — направления актуализированы (были битые
      // /race и /coach-chat), добавлены Путь/Спарринг/Физиология/KB/чек-ин.
      {
        id: "today",
        label: t("cmd.today", { defaultValue: "Сегодня" }),
        hint: t("cmd.today_hint", { defaultValue: "Кабина дня · готовность" }),
        keywords: ["today", "сегодня", "main", "главный", "readiness"],
        path: "/",
      },
      {
        id: "path",
        label: t("cmd.path", { defaultValue: "Путь" }),
        hint: t("cmd.path_hint", { defaultValue: "Цель · сезон · история" }),
        keywords: ["path", "путь", "goal", "цель", "сезон", "история"],
        path: "/path",
      },
      {
        id: "plan",
        label: t("cmd.plan", { defaultValue: "План" }),
        hint: t("cmd.plan_hint", { defaultValue: "Тренировки и фазы" }),
        keywords: ["plan", "план", "тренировки", "schedule", "микроцикл"],
        path: "/plan",
      },
      {
        id: "events",
        label: t("cmd.events", { defaultValue: "Старты" }),
        hint: t("cmd.events_hint", { defaultValue: "Подбор гонок" }),
        keywords: ["events", "старты", "гонки", "races", "calendar"],
        path: "/events",
      },
      {
        id: "race",
        label: t("cmd.race", { defaultValue: "Гонка" }),
        hint: t("cmd.race_hint", { defaultValue: "Центр недели гонки" }),
        keywords: ["race", "гонка", "старт", "raceweek", "pacing", "fueling"],
        path: "/race-week",
      },
      {
        id: "analytics",
        label: t("cmd.workstation", { defaultValue: "Анализ" }),
        hint: t("cmd.workstation_hint", { defaultValue: "Обзор · 27 линз · сигналы" }),
        keywords: ["workstation", "analytics", "indigo", "ws", "аналитика", "линзы", "lenses", "pmc"],
        path: "/analytics",
      },
      {
        id: "physiology",
        label: t("cmd.physiology", { defaultValue: "Физиология" }),
        hint: t("cmd.physiology_hint", { defaultValue: "Пороги · зоны · предсказания" }),
        keywords: ["physiology", "физиология", "пороги", "threshold", "zones", "зоны", "vdot", "ftp"],
        path: "/analytics",
      },
      {
        id: "checkin",
        label: t("cmd.checkin", { defaultValue: "Чек-ин" }),
        hint: t("cmd.checkin_hint", { defaultValue: "Утренние показатели · 20 сек" }),
        keywords: ["checkin", "чек-ин", "wellness", "самочувствие", "hrv", "сон"],
        path: "/wellness/checkin",
      },
      {
        id: "sparring",
        label: t("cmd.sparring", { defaultValue: "Спарринг" }),
        hint: t("cmd.sparring_hint", { defaultValue: "Дуэли · челленджи · пара" }),
        keywords: ["sparring", "спарринг", "дуэль", "duel", "challenge", "челлендж", "пара", "друзья"],
        path: "/sparring",
      },
      {
        id: "reflections",
        label: t("cmd.reflections", { defaultValue: "Заметки" }),
        hint: t("cmd.reflections_hint", { defaultValue: "Взгляд назад · история" }),
        keywords: ["reflections", "рефлексия", "история", "chronicle"],
        path: "/reflections",
      },
      {
        id: "kb",
        label: t("cmd.kb", { defaultValue: "Знания" }),
        hint: t("cmd.kb_hint", { defaultValue: "База знаний · статьи метода" }),
        keywords: ["kb", "знания", "knowledge", "статьи", "articles", "метод"],
        path: "/knowledge",
      },
      {
        id: "diary",
        label: t("cmd.diary", { defaultValue: "Дневник" }),
        hint: t("cmd.diary_hint", { defaultValue: "Заметки о тренировках" }),
        keywords: ["diary", "дневник", "notes", "заметки"],
        path: "/diary",
      },
      {
        id: "chat",
        label: t("cmd.chat", { defaultValue: "Чат с тренером" }),
        hint: t("cmd.chat_hint", { defaultValue: "Тренер · сообщения" }),
        keywords: ["chat", "чат", "coach", "тренер", "message"],
        path: "/chat",
      },
      {
        id: "settings",
        label: t("cmd.settings", { defaultValue: "Настройки" }),
        hint: t("cmd.settings_hint", { defaultValue: "Профиль · интеграции" }),
        keywords: ["settings", "настройки", "profile", "integrations"],
        path: "/settings",
      },
      {
        id: "profile",
        label: t("cmd.profile", { defaultValue: "Профиль" }),
        hint: t("cmd.profile_hint", { defaultValue: "Я и моё" }),
        keywords: ["profile", "профиль", "me", "снаряжение", "gear"],
        path: "/profile",
      },
      {
        id: "search",
        label: t("cmd.search", { defaultValue: "Поиск по платформе" }),
        hint: t("cmd.search_hint", {
          defaultValue: "Тренировки, дневник, статьи",
        }),
        keywords: ["search", "поиск", "find", "найти"],
        path: "/search",
      },
    ],
    [t],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (it) =>
        it.label.toLowerCase().includes(q) ||
        it.hint.toLowerCase().includes(q) ||
        it.keywords.some((k) => k.toLowerCase().includes(q)),
    );
  }, [query, items]);

  // Reset active index when filter changes so the highlight doesn't
  // point past the end of the new list.
  useEffect(() => {
    setActiveIdx(0);
  }, [query]);

  const select = (idx: number) => {
    const it = filtered[idx];
    if (!it) {
      // PH9-7: unmapped query falls through to /search with the typed text
      const q = query.trim();
      if (q.length >= 2) {
        nav(`/search?q=${encodeURIComponent(q)}`);
        setOpen(false);
      }
      return;
    }
    nav(it.path);
    setOpen(false);
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="athlete-command-bar"
      className="fixed inset-0 z-50 flex items-start justify-center bg-bg-base/70 backdrop-blur-sm pt-[20vh]"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="w-full max-w-md rounded-card bg-bg-card shadow-drawer overflow-hidden">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) => Math.min(filtered.length - 1, i + 1));
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) => Math.max(0, i - 1));
            } else if (e.key === "Enter") {
              e.preventDefault();
              select(activeIdx);
            }
          }}
          placeholder={t("cmd.placeholder", {
            defaultValue: "Найти страницу… (Cmd-K)",
          })}
          aria-label={t("cmd.aria_input", { defaultValue: "Command search" })}
          data-testid="command-bar-input"
          className="w-full px-4 py-3 text-sm bg-transparent text-fg-primary placeholder:text-fg-muted outline-none border-b border-bg-border"
        />
        <ul role="listbox" className="max-h-80 overflow-y-auto">
          {filtered.length === 0 ? (
            <li
              className="px-4 py-3 text-sm text-fg-muted"
              data-testid="command-bar-empty"
            >
              {t("cmd.no_results", { defaultValue: "Ничего не найдено" })}
            </li>
          ) : (
            filtered.map((it, idx) => (
              <li
                key={it.id}
                role="option"
                aria-selected={idx === activeIdx}
                data-testid={`command-bar-item-${it.id}`}
                data-active={idx === activeIdx ? "true" : "false"}
                onMouseEnter={() => setActiveIdx(idx)}
                onClick={() => select(idx)}
                className={[
                  "px-4 py-2.5 cursor-pointer flex items-baseline justify-between gap-3",
                  idx === activeIdx ? "bg-bg-inner" : "",
                ].join(" ")}
              >
                <span className="text-sm font-medium text-fg-primary">{it.label}</span>
                <span className="text-[11px] text-fg-muted">{it.hint}</span>
              </li>
            ))
          )}
        </ul>
      </div>
    </div>
  );
}
